// Pure state machine for the conversation-history load-failed and loading
// indicators driven by Chat.tsx's loadConversation, delete-conversation, and
// new-conversation flows. Extracted out of Chat.tsx (which cannot be
// rendered directly in a unit test without mocking ~20 modules) so the
// transitions themselves can be tested, including generation races between
// two in-flight loads and the interaction between a load failure and a
// later delete -- the #1345 defect, where historyLoadFailed was never reset
// when the failed conversation was deleted.

export interface HistoryLoadState {
  loadFailedConversationId: number | null;
  loading: boolean;
  latestGeneration: number;
}

export const initialHistoryLoadState: HistoryLoadState = {
  loadFailedConversationId: null,
  loading: false,
  latestGeneration: 0,
};

export type HistoryLoadEvent =
  | { type: "loadStarted"; conversationId: number; generation: number }
  | { type: "loadSucceeded"; conversationId: number; generation: number }
  | {
      type: "loadFailed";
      conversationId: number;
      generation: number;
      // False for an ignorable error (currently: "not configured", which
      // fires while ChatProvider is still wiring up and is retried
      // automatically once it finishes -- it must still clear `loading`,
      // but must not paint the load-failed surface).
      markFailed: boolean;
    }
  | { type: "conversationDeleted"; conversationId: number; generation: number }
  | { type: "newConversation"; generation: number };

// `generation` is the caller's monotonically increasing counter (Chat.tsx's
// messageLoadGenerationRef). loadStarted/conversationDeleted/newConversation
// always adopt the generation they carry, since each corresponds to a point
// where the caller already bumped its own counter. loadSucceeded/loadFailed
// are results of a previously started load and only apply if their
// generation still matches the latest one seen -- otherwise a slower,
// superseded load's result is silently dropped rather than corrupting the
// state a newer load (or a delete/new-conversation reset) already
// established.
export function reduceHistoryLoadState(
  state: HistoryLoadState,
  event: HistoryLoadEvent,
): HistoryLoadState {
  switch (event.type) {
    case "loadStarted":
      return {
        ...state,
        loading: true,
        latestGeneration: event.generation,
      };

    case "loadSucceeded": {
      if (event.generation !== state.latestGeneration) return state;
      return {
        loadFailedConversationId: null,
        loading: false,
        latestGeneration: state.latestGeneration,
      };
    }

    case "loadFailed": {
      if (event.generation !== state.latestGeneration) return state;
      return {
        loadFailedConversationId: event.markFailed
          ? event.conversationId
          : state.loadFailedConversationId,
        loading: false,
        latestGeneration: state.latestGeneration,
      };
    }

    case "conversationDeleted":
      return {
        loadFailedConversationId:
          state.loadFailedConversationId === event.conversationId
            ? null
            : state.loadFailedConversationId,
        loading: state.loading,
        latestGeneration: event.generation,
      };

    case "newConversation":
      return {
        loadFailedConversationId: null,
        loading: false,
        latestGeneration: event.generation,
      };

    default:
      return state;
  }
}
